(() => {
  const groups = new WeakSet();
  const animations = new WeakMap();
  const desiredStates = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const duration = 300;
  const easing = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  const getParts = (details) => {
    const summary = details.querySelector(':scope > summary');
    const panelId = summary?.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    return { summary, panel };
  };

  const clearAnimationStyles = (panel) => {
    panel.style.removeProperty('height');
    panel.style.removeProperty('overflow');
    panel.style.removeProperty('opacity');
    panel.style.removeProperty('margin-top');
    panel.style.removeProperty('margin-bottom');
  };

  const finishImmediately = (details, shouldOpen) => {
    const { summary, panel } = getParts(details);
    animations.get(details)?.cancel();
    animations.delete(details);
    desiredStates.set(details, shouldOpen);
    details.open = shouldOpen;
    summary?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    if (panel) clearAnimationStyles(panel);
  };

  const animateDetails = (details, shouldOpen) => {
    const { summary, panel } = getParts(details);
    if (!summary || !panel || reducedMotion.matches || typeof panel.animate !== 'function') {
      finishImmediately(details, shouldOpen);
      return;
    }

    const previousAnimation = animations.get(details);
    const startHeight = panel.getBoundingClientRect().height;
    const currentStyles = window.getComputedStyle(panel);
    const startMarginTop = details.open ? currentStyles.marginTop : '0px';
    const startMarginBottom = details.open ? currentStyles.marginBottom : '0px';
    previousAnimation?.cancel();
    desiredStates.set(details, shouldOpen);
    summary.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

    if (shouldOpen) details.open = true;

    const naturalStyles = window.getComputedStyle(panel);
    const endMarginTop = shouldOpen ? naturalStyles.marginTop : '0px';
    const endMarginBottom = shouldOpen ? naturalStyles.marginBottom : '0px';
    panel.style.height = `${startHeight}px`;
    panel.style.overflow = 'hidden';
    panel.style.marginTop = startMarginTop;
    panel.style.marginBottom = startMarginBottom;
    const endHeight = shouldOpen ? panel.scrollHeight : 0;
    const animation = panel.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
        opacity: [shouldOpen ? 0.72 : 1, shouldOpen ? 1 : 0.72],
        marginTop: [startMarginTop, endMarginTop],
        marginBottom: [startMarginBottom, endMarginBottom]
      },
      { duration, easing }
    );

    animations.set(details, animation);
    animation.onfinish = () => {
      if (animations.get(details) !== animation) return;
      if (!shouldOpen) details.open = false;
      clearAnimationStyles(panel);
      animations.delete(details);
    };
    animation.oncancel = () => {
      if (animations.get(details) === animation) animations.delete(details);
    };
  };

  const initializeGroup = (group) => {
    if (groups.has(group)) return;
    groups.add(group);

    const items = [...group.querySelectorAll('details[data-accordion-item]')].filter(
      (details) => details.closest('[data-accordion-group]') === group
    );
    let hasOpenItem = false;

    items.forEach((details) => {
      const { summary } = getParts(details);
      if (!summary) return;

      if (details.open && hasOpenItem) details.open = false;
      if (details.open) hasOpenItem = true;
      desiredStates.set(details, details.open);
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', () => {
        if (animations.has(details)) return;
        desiredStates.set(details, details.open);
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = !(desiredStates.get(details) ?? details.open);

        if (shouldOpen) {
          items.forEach((item) => {
            if (item !== details && (desiredStates.get(item) || item.open)) animateDetails(item, false);
          });
        }

        animateDetails(details, shouldOpen);
      });
    });
  };

  const initializeGroups = (root = document) => {
    if (root.matches?.('[data-accordion-group]')) initializeGroup(root);
    root.querySelectorAll?.('[data-accordion-group]').forEach(initializeGroup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeGroups(), { once: true });
  } else {
    initializeGroups();
  }

  document.addEventListener('shopify:section:load', (event) => initializeGroups(event.target));
})();
